import { html, LitElement, nothing } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { ref } from 'lit/directives/ref.js'
import { LightDomController } from '../common/controllers/LightDomController.js'
import { LightSlotController } from '../common/controllers/LightSlotController.js'
import { observe } from '../common/decorators/observe.js'
import { cond } from '../common/directives/cond.js'
import { wrapIf } from '../common/directives/wrapIf.js'

import { FocusableMixin } from '../common/mixins/FocusableMixin.js'
import { FormAssociatedMixin } from '../common/mixins/FormAssociatedMixin.js'
import { InputMixin } from '../common/mixins/InputMixin.js'
import { SizeMixin } from '../common/mixins/SizeMixin.js'

import componentStyle from '../common/styles/Component.css'
import formFieldStyle from '../common/styles/FormField.css'
import style from './Radio.css'

let id = 0
const createId = (suffix: string) => `nord-radio-${suffix}-${id++}`

function isLabel(element: Element): element is HTMLLabelElement {
  return element.localName === 'label'
}

/**
 * Force reflow (and with it a repaint) of a radio button because of a macOS Safari rendering bug
 *
 * @see https://bugs.webkit.org/show_bug.cgi?id=249706
 */
function forceReflow(radio: Radio) {
  radio.style.display = 'none'
  // eslint-disable-next-line ts/no-unused-expressions
  radio.offsetHeight // no need to store this anywhere, the reference is enough
  radio.style.display = 'inline-block'
}

/**
 * Radio buttons are graphical user interface elements that allow user to choose only one option from
 * a predefined set of mutually exclusive options.
 *
 * @status ready
 * @category form
 * @slot label - Use when a label requires more than plain text.
 * @slot hint - Optional slot that holds hint text for the input.
 * @slot error - Optional slot that holds error text for the input.
 *
 * @cssprop [--n-label-color=var(--n-color-text)] - Controls the text color of the label, using our [color tokens](/tokens/#color).
 */
@customElement('nord-radio')
export default class Radio extends SizeMixin(FormAssociatedMixin(InputMixin(FocusableMixin(LitElement)))) {
  static styles = [componentStyle, formFieldStyle, style]

  protected override inputId = createId('input')
  protected override hintId = createId('hint')
  protected override errorId = createId('error')

  /**
   * For accessibility reasons, we render some parts of the component to the light DOM.
   */
  protected override hintSlot = new LightSlotController(this, {
    slotName: 'hint',
    render: () => (this.hint ? html`<div slot="hint-internal" id=${this.hintId}>${this.hint}</div>` : nothing),
    syncLightDom: (element) => {
      element.id = this.hintId
    },
  })

  protected override labelSlot = new LightSlotController(this, {
    slotName: 'label',
    render: () => (this.label ? html`<label slot="label-internal" for=${this.inputId}>${this.label}</label>` : nothing),
    syncLightDom: (element) => {
      if (!isLabel(element)) {
        console.warn(`NORD: Only <label> elements should be placed in radio's "label" slot`)
      }
      else {
        element.htmlFor = this.inputId
      }
    },
  })

  protected override errorSlot = new LightSlotController(this, {
    slotName: 'error',
    render: () => (this.error ? html`<div slot="error-internal" id=${this.errorId}>${this.error}</div>` : nothing),
    syncLightDom: (element) => {
      element.id = this.hintId
    },
  })

  protected inputSlot = new LightDomController(this, {
    render: () => html`
      <input
        slot="input"
        @blur=${this.handleBlur}
        @focus=${this.handleFocus}
        ${ref(this.focusableRef)}
        class="n-input"
        id=${this.inputId}
        type="radio"
        name=${cond(this.name)}
        .value=${cond(this.value)}
        .checked=${this.checked}
        ?disabled=${this.effectiveDisabled}
        ?required=${this.required}
        aria-label=${cond(this.controlAriaLabel)}
        aria-describedby=${cond(this.controlAriaDescribedby)}
        aria-invalid=${cond(this.controlAriaInvalid)}
        form=${cond(this._formId)}
      />
    `,
  })

  protected override get formValue() {
    // opt out of formdata event, since radio button is in light dom
    return undefined
  }

  /**
   * Controls whether the checkbox is checked or not.
   */
  @property({ type: Boolean, reflect: true }) checked: boolean = false

  render() {
    const hint = html`
      <div class="n-caption n-hint" ?hidden=${!this.hasHint}>
        <slot name="hint"></slot>
        <slot name="hint-internal"></slot>
      </div>
    `
    const error = html`
      <div class="n-caption n-error" role="alert" ?hidden=${!this.hasError}>
        <slot name="error"></slot>
        <slot name="error-internal"></slot>
      </div>
    `

    return html`
      <div class="n-flex">
        <div class="n-input-container" @change=${this.handleChange}>
          <slot name="input"></slot>
          ${this.checked ? html`<div class="n-dot"></div>` : nothing}
        </div>
        <div class="n-expand">
          <div class="n-label-container">
            ${this.insideField
              ? nothing
              : wrapIf(
                  this.hideLabel,
                  () => html`
                    <slot name="label"></slot>
                    <slot name="label-internal"></slot>
                  `,
                  content => html`<nord-visually-hidden>${content}</nord-visually-hidden>`,
                )}
            ${this.isHintBelow ? nothing : hint}
          </div>
          ${this.isHintBelow ? nothing : error}
        </div>
      </div>
      ${this.isHintBelow ? error : nothing}
      ${this.isHintBelow ? hint : nothing}
    `
  }

  @observe('checked')
  protected handleCheckedChange(previousChecked: boolean) {
    // if this component was previous unchecked but is now checked,
    // then we need to uncheck any radios in the same group
    if (!previousChecked && this.checked) {
      this.uncheckSiblings()
    }
  }

  private uncheckSiblings() {
    const root = this.getRootNode() as Document | ShadowRoot

    root.querySelectorAll<Radio>(`nord-radio[name="${this.name}"]`).forEach((radio) => {
      // Reflow every currently-checked radio in the group — including `this`,
      // the newly-checked one — before unchecking the others. macOS Safari
      // leaves the just-checked radio unpainted when selection moves via the
      // up/down arrow keys; reflowing it (not only the one being unchecked)
      // forces the repaint. See https://bugs.webkit.org/show_bug.cgi?id=249706
      if (radio.checked) {
        forceReflow(radio)
      }

      if (radio !== this) {
        radio.checked = false
      }
    })
  }

  protected handleChange(e: Event): void {
    e.stopPropagation()
    const target = e.target as HTMLInputElement

    this.checked = target.checked
    super.handleChange(e)
  }

  private handleBlur = (e: Event) => {
    e.stopPropagation()
    this.dispatchEvent(new Event('blur', { bubbles: false, cancelable: true }))
  }

  private handleFocus = (e: Event) => {
    e.stopPropagation()
    this.dispatchEvent(new Event('focus', { bubbles: false, cancelable: true }))
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-radio': Radio
  }
}
